import { z } from 'zod';
import { assetCategorySchema, departmentSchema, intervalTypeSchema, ticketPrioritySchema, assignableByAdminSchema, } from '../enums.js';
/** Admin management contracts (SPEC.md §5.2, §5.4). */
// --- locations ---------------------------------------------------------------
export const createLocationBodySchema = z.object({
    name: z.string().trim().min(1).max(120),
    floor: z.string().trim().max(40).nullable().optional(),
    qrCode: z.string().trim().min(1).max(80).nullable().optional(),
    parentId: z.string().min(1).nullable().optional(),
    department: departmentSchema.nullable().optional(),
});
export const updateLocationBodySchema = createLocationBodySchema.partial().extend({
    isActive: z.boolean().optional(),
});
/**
 * Batch room creation. A hotel floor is twenty near-identical rooms, and adding them
 * one at a time is how data entry gets abandoned half-finished.
 */
export const batchCreateLocationsBodySchema = z
    .object({
    parentId: z.string().min(1),
    /** Rendered as `${prefix}${n}`, e.g. "Room " + 101. */
    namePrefix: z.string().trim().max(40).default('Room '),
    from: z.number().int().min(0).max(99_999),
    to: z.number().int().min(0).max(99_999),
    floor: z.string().trim().max(40).nullable().optional(),
    department: departmentSchema.nullable().optional(),
    /** QR token per room, e.g. "room-101". Omit to leave rooms without stickers. */
    qrPrefix: z.string().trim().max(40).nullable().optional(),
})
    .refine((value) => value.to >= value.from, {
    message: 'The end of the range must not be before the start',
    path: ['to'],
})
    .refine((value) => value.to - value.from < 500, {
    // A typo like 1 to 99999 would otherwise insert a hundred thousand rooms.
    message: 'Create at most 500 rooms at a time',
    path: ['to'],
});
// --- assets ------------------------------------------------------------------
export const createAssetBodySchema = z.object({
    name: z.string().trim().min(1).max(160),
    category: assetCategorySchema.default('OTHER'),
    manufacturer: z.string().trim().max(120).nullable().optional(),
    model: z.string().trim().max(120).nullable().optional(),
    serialNumber: z.string().trim().max(120).nullable().optional(),
    qrCode: z.string().trim().max(80).nullable().optional(),
    locationId: z.string().min(1).nullable().optional(),
    installedAt: z.coerce.date().nullable().optional(),
    warrantyExpiresAt: z.coerce.date().nullable().optional(),
});
/**
 * Retirement body, shared by assets and PPM schedules.
 *
 * The reason is mandatory. A record that simply vanished from the active list, with
 * no explanation, is the thing that makes an audit trail useless six months later.
 */
export const retireBodySchema = z.object({
    retiredReason: z.string().trim().min(3, 'Say why this is being retired').max(500),
});
// --- ppm schedules -----------------------------------------------------------
export const createPpmScheduleBodySchema = z
    .object({
    title: z.string().trim().min(1).max(160),
    description: z.string().trim().max(2000).nullable().optional(),
    department: departmentSchema,
    priority: ticketPrioritySchema.default('MEDIUM'),
    assetId: z.string().min(1).nullable().optional(),
    locationId: z.string().min(1),
    checklistTemplateId: z.string().min(1),
    intervalType: intervalTypeSchema,
    intervalValue: z.number().int().min(1).max(365).default(1),
    nextDueDate: z.coerce.date(),
    meterThreshold: z.number().positive().nullable().optional(),
})
    .refine((value) => value.intervalType !== 'METER_BASED' || value.meterThreshold != null, {
    // A meter-based schedule with no threshold would never become due.
    message: 'A meter-based schedule needs a meter threshold',
    path: ['meterThreshold'],
});
// Assets and PPM schedules are create-and-retire: there is deliberately no update
// body for either. Editing a live schedule desynchronises the sweep mid-cycle, and
// editing an asset rewrites the subject of tickets already raised against it.
// Replacing one means retiring it and creating its successor.
// --- checklist templates -----------------------------------------------------
export const checklistTemplateItemSchema = z.object({
    instruction: z.string().trim().min(1).max(500),
    isRequired: z.boolean().default(true),
    /**
     * Marks a step as expecting photo proof. It does **not** gate the tick —
     * decision D12 — so a technician whose camera will not open can still sign off
     * work they have genuinely done. Missing proof is reported, never enforced.
     */
    requirePhoto: z.boolean().default(false),
});
export const upsertChecklistTemplateBodySchema = z.object({
    title: z.string().trim().min(3, 'Give the checklist a recognisable title').max(160),
    description: z.string().trim().max(2000).nullable().optional(),
    department: departmentSchema,
    /**
     * Order is the array order. stepNumber is derived on the server rather than sent,
     * so a reorder cannot produce gaps or duplicate positions.
     */
    items: z
        .array(checklistTemplateItemSchema)
        .min(1, 'A checklist needs at least one step')
        .max(100),
});
// --- users -------------------------------------------------------------------
/**
 * There is no `createUserBodySchema` any more (D44).
 *
 * `POST /admin/users` took one, and it was the second route on the root tier that could
 * mint an account — the one that wrote no audit row. It is gone;
 * `createPrivilegedUserBodySchema` in `./super-admin` is the only creation contract, it
 * takes the full role range, and the route behind it records who created what.
 *
 * `assignableByAdminSchema` below is not vestigial in its absence. `PUT /admin/users/:id`
 * still edits roles, and withholding SUPER_ADMIN there is what sends a promotion to the
 * audited route rather than through a general-purpose patch.
 */
export const updateUserBodySchema = z.object({
    name: z.string().trim().min(1).max(120).optional(),
    /** Same withholding as creation: an admin cannot promote anyone to root (D23). */
    role: assignableByAdminSchema.optional(),
    /** Optional here only because this is a patch; it can never be set to null. */
    department: departmentSchema.optional(),
    isActive: z.boolean().optional(),
});
//# sourceMappingURL=admin.js.map